"use client"; // This component needs client-side interactivity (state, effects, fetch)

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { z } from 'zod'; // For potential frontend validation if needed, though primary validation is backend

// Import UI components from Shadcn
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"; // Import Select
import { Label } from "@/components/ui/label"; // Import Label
import { Terminal } from "lucide-react"; // Example icon

// Import the data types/schemas defined in our API routes
// Adjust paths if necessary based on your project structure
import { type FinancialData } from '@/app/api/(plugins)/ai-hedgefund/aiHedgeFundTypes';
import { type TechnicalData } from '@/app/api/(plugins)/ai-hedgefund/aiHedgeFundTypes';
import { type SentimentData } from '@/app/api/(plugins)/ai-hedgefund/aiHedgeFundTypes';
// Import AgentType along with InsightData
import { type InsightData, InsightDataSchema, type AgentType } from '@/app/api/(plugins)/ai-hedgefund/aiHedgeFundTypes';

/**
 * @uiComponent
 * @name AIHedgeFund
 * @description The AI Hedge Fund component provides sophisticated stock analysis using AI-powered personas.
 * It allows users to input stock tickers and select different analysis personas (like Warren Buffett)
 * to get investment recommendations with supporting financial, technical, and sentiment data.
 * Features real-time data fetching, comprehensive analysis display, and persona-based insights.
 * @location AI Hedge Fund plugin in the trading terminal
 * @prop {string} ticker - The stock ticker symbol passed from the parent component
 */

/**
 * @uiWorkflow
 * @name Analyze Stock with AI Hedge Fund
 * @description How to get AI-powered investment analysis for any stock ticker
 * @platform ai-hedgefund
 * @step 1 Navigate to the AI Hedge Fund plugin [component: AIHedgeFund] [action: navigate]
 * @step 2 Enter a stock ticker symbol (e.g., AAPL, MSFT) [component: AIHedgeFund] [action: type]
 * @step 3 Select an analysis persona from the dropdown [component: AIHedgeFund] [action: select] [notes: Choose from Default, Warren Buffett, etc.]
 * @step 4 Click the "Analyze" button to start analysis [component: AIHedgeFund] [action: click]
 * @step 5 Wait for the AI analysis to complete [component: AIHedgeFund] [action: wait] [notes: Loading indicator shows progress]
 * @step 6 Review the investment decision (Buy/Sell/Hold) [component: AIHedgeFund] [action: observe]
 * @step 7 Read the detailed reasoning behind the recommendation [component: AIHedgeFund] [action: read]
 * @step 8 Examine supporting financial metrics and ratios [component: AIHedgeFund] [action: scroll]
 * @step 9 Check technical indicators like RSI and MACD [component: AIHedgeFund] [action: observe]
 * @step 10 Review sentiment analysis from news and social media [component: AIHedgeFund] [action: observe]
 */

/**
 * @uiWorkflow
 * @name Compare Analysis Across Different AI Personas
 * @description How to get multiple perspectives on the same stock using different AI personas
 * @platform ai-hedgefund
 * @step 1 Enter your desired stock ticker in the input field [component: AIHedgeFund] [action: type]
 * @step 2 Select "Default" persona and click Analyze [component: AIHedgeFund] [action: select, click]
 * @step 3 Note the investment decision and reasoning [component: AIHedgeFund] [action: observe]
 * @step 4 Change the persona to "Warren Buffett" [component: AIHedgeFund] [action: select] [notes: Analysis will automatically refresh]
 * @step 5 Compare the new decision with the previous one [component: AIHedgeFund] [action: compare]
 * @step 6 Analyze differences in reasoning and approach [component: AIHedgeFund] [action: read]
 * @step 7 Review how different personas weight various factors [component: AIHedgeFund] [action: observe]
 * @step 8 Use insights to form your own investment opinion [component: AIHedgeFund] [action: analyze]
 */

/**
 * @uiWorkflow
 * @name Monitor Real-time Stock Analysis Updates
 * @description How to track changes in AI recommendations as market conditions evolve
 * @platform ai-hedgefund
 * @step 1 Set up analysis for your target stock ticker [component: AIHedgeFund] [action: type, click]
 * @step 2 Bookmark the current recommendation and confidence level [component: AIHedgeFund] [action: observe]
 * @step 3 Note the key financial metrics displayed [component: AIHedgeFund] [action: record] [notes: Market cap, P/E ratio, etc.]
 * @step 4 Check technical indicators like RSI and MACD values [component: AIHedgeFund] [action: observe]
 * @step 5 Refresh analysis periodically by clicking Analyze again [component: AIHedgeFund] [action: click]
 * @step 6 Compare new results with previous analysis [component: AIHedgeFund] [action: compare]
 * @step 7 Track changes in sentiment scores over time [component: AIHedgeFund] [action: monitor]
 * @step 8 Adjust investment strategy based on trend changes [component: AIHedgeFund] [action: decide]
 */

// --- Helper Functions for Formatting ---

// Formats numbers as currency (USD)
const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return 'N/A';
  // Simple formatting, consider using Intl.NumberFormat for more robust localization
  return `$${(value / 1_000_000_000).toFixed(2)}B`; // Example: Format as billions
};

// Formats numbers as percentages
const formatPercentage = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return 'N/A';
  return `${(value * 100).toFixed(2)}%`;
};

// Formats numbers with fixed decimal places
const formatNumber = (value: number | null | undefined, decimals: number = 2): string => {
  if (value === null || value === undefined) return 'N/A';
  return value.toFixed(decimals);
};

// --- Component Props Interface ---

// Define the props the component will accept
interface AIHedgeFundProps {
  ticker: string; // The stock ticker symbol passed from the parent
}

// --- Main Component ---

// Update the component definition to accept props
const AIHedgeFund = ({ ticker }: AIHedgeFundProps) => {
  // State for the ticker symbol input - Initialize with the passed ticker
  const [tickerInput, setTickerInput] = useState<string>(ticker || '');
  // State for the ticker symbol submitted for analysis - Initialize with the passed ticker
  const [submittedTicker, setSubmittedTicker] = useState<string>(ticker || '');
  // State for the selected agent persona
  const [selectedAgent, setSelectedAgent] = useState<AgentType>('Default'); // Default agent
  // State to hold the fetched insight data
  const [insightData, setInsightData] = useState<InsightData | null>(null);
  // State to track loading status
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State to store any errors during fetching
  const [error, setError] = useState<string | null>(null);

  // Function to handle fetching data from the insight API endpoint
  // Now includes the agent parameter
  const fetchDataAndInsight = useCallback(async (tickerToFetch: string, agent: AgentType) => {
    // Check if ticker is provided
    if (!tickerToFetch) return;

    // Set loading state and clear previous errors/data
    setIsLoading(true);
    setError(null);
    // Keep previous data while loading new data for a smoother experience
    // setInsightData(null); 
    console.log(`[AIHedgeFund] Fetching insight for: ${tickerToFetch} with Agent: ${agent}`);

    try {
      // Construct the API URL with both ticker and agent parameters
      const apiUrl = `/api/ai-hedgefund/insight?ticker=${encodeURIComponent(tickerToFetch)}&agent=${encodeURIComponent(agent)}`;
      // Fetch data from the insight endpoint
      const response = await fetch(apiUrl);

      // Handle HTTP errors
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to parse error response' }));
        console.error(`[AIHedgeFund] API Error (${response.status}):`, errorData);
        throw new Error(errorData?.error || `Failed to fetch data. Status: ${response.status}`);
      }

      // Parse the JSON response
      const rawData: unknown = await response.json();
      console.log(`[AIHedgeFund] Raw data received for ${tickerToFetch} (Agent: ${agent}):`, rawData);

      // Validate the received data against the Zod schema
      const validationResult = InsightDataSchema.safeParse(rawData);

      // Handle validation errors
      if (!validationResult.success) {
        console.error(`[AIHedgeFund] Failed to parse API response for ${tickerToFetch} (Agent: ${agent}):`, validationResult.error.flatten());
        throw new Error('Received invalid data structure from the server.');
      }

      // Update state with the validated data
      setInsightData(validationResult.data);
      console.log(`[AIHedgeFund] Successfully fetched and parsed data for ${tickerToFetch} (Agent: ${agent})`);

    } catch (err: any) {
      // Handle fetch or validation errors
      console.error('[AIHedgeFund] Error fetching or processing insight:', err);
      setError(err.message || 'An unexpected error occurred.'); // Set error message state
    } finally {
      // Reset loading state
      setIsLoading(false);
    }
    // Dependencies for useCallback: none, as it doesn't rely on external state/props directly
  }, []);

  // Handler for the input change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Store ticker as uppercase
    setTickerInput(event.target.value.toUpperCase());
  };

  // Handler for agent selection change
  const handleAgentChange = (value: string) => {
    // Update the selected agent state. We cast here, assuming value matches AgentType.
    // Add validation if needed, but Select should only provide valid options.
    setSelectedAgent(value as AgentType);
  };

  // Handler for the form submission (button click)
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    // Prevent default form submission behavior
    event.preventDefault();
    // Set the ticker to be analyzed if it's not empty
    if (tickerInput.trim()) {
      setSubmittedTicker(tickerInput.trim());
    }
  };

  // Effect to trigger data fetching when the submittedTicker or selectedAgent changes
  // Also fetch data on initial mount if a ticker is provided via props
  useEffect(() => {
    // Fetch data only if a ticker has been submitted (or initially provided)
    if (submittedTicker) {
      fetchDataAndInsight(submittedTicker, selectedAgent);
    }
    // Dependencies: trigger fetch when ticker or agent changes
  }, [submittedTicker, selectedAgent, fetchDataAndInsight]);

  // Memoize the extracted data pieces to avoid recalculating on every render
  const financials = useMemo(() => insightData?.supportingData?.financials, [insightData]);
  const technicals = useMemo(() => insightData?.supportingData?.technicals, [insightData]);
  const sentiment = useMemo(() => insightData?.supportingData?.sentiment, [insightData]);

  // --- Render Logic ---
  return (
    <div className="p-4 space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>AI Hedge Fund Analysis</CardTitle>
          <CardDescription>Enter a stock ticker and select an analysis persona.</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Input form for ticker and agent selection */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Ticker Input */}
            <div className="flex items-end space-x-2">
              <div className="flex-grow">
                 <Label htmlFor="ticker-input">Stock Ticker</Label>
                 <Input
                   id="ticker-input"
                   type="text"
                   placeholder="e.g., AAPL, MSFT"
                   value={tickerInput}
                   onChange={handleInputChange}
                   aria-label="Stock Ticker Symbol"
                 />
              </div>
              {/* Agent Selector */}
              <div className="w-48"> {/* Adjust width as needed */}
                 <Label htmlFor="agent-select">Analysis Persona</Label>
                 <Select value={selectedAgent} onValueChange={handleAgentChange}>
                   <SelectTrigger id="agent-select" aria-label="Select Analysis Persona">
                     <SelectValue placeholder="Select Persona" />
                   </SelectTrigger>
                   <SelectContent>
                     {/* Define selectable agent options */}
                     <SelectItem value="Default">Default</SelectItem>
                     <SelectItem value="Buffett">Warren Buffett</SelectItem>
                     {/* Add more agents here */}
                   </SelectContent>
                 </Select>
              </div>
            </div>
             {/* Submit Button */}
             <Button type="submit" disabled={isLoading || !tickerInput.trim()}>
               {isLoading ? 'Analyzing...' : 'Analyze'}
             </Button>
          </form>
        </CardContent>
      </Card>

      {/* Display Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center p-4">
          <Terminal className="h-5 w-5 animate-spin mr-2" />
          {/* Indicate which ticker and agent are being analyzed */}
          <span>Loading {selectedAgent} analysis for {submittedTicker}...</span>
        </div>
      )}

      {/* Display Error State */}
      {error && !isLoading && (
        <Alert variant="destructive">
          <Terminal className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Display Insight Data when available and not loading */}
      {insightData && !isLoading && (
        <div className="space-y-4">
          {/* Main Insight Card */}
          <Card>
            <CardHeader>
              {/* Include agent in the title */}
              <CardTitle>{selectedAgent} Analysis for {insightData.ticker}</CardTitle>
              <CardDescription>Generated insight based on available data.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-lg font-semibold">
                Decision: <span className={`
                  ${insightData.decision === 'Buy' ? 'text-green-600' : ''}
                  ${insightData.decision === 'Sell' ? 'text-red-600' : ''}
                  ${insightData.decision === 'Hold' ? 'text-yellow-600' : ''}
                `}>{insightData.decision}</span>
                {insightData.confidence !== undefined && (
                  <span className="text-sm text-muted-foreground ml-2">(Confidence: {formatPercentage(insightData.confidence)})</span>
                )}
              </p>
              <p><span className="font-semibold">Reason:</span> {insightData.reason}</p>
            </CardContent>
          </Card>

          {/* Supporting Data Sections (Financials, Technicals, Sentiment) */}
          {/* ... No changes needed here, they display data from insightData.supportingData ... */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Financials Card */}
            <Card>
              <CardHeader>
                <CardTitle>Financial Snapshot</CardTitle>
              </CardHeader>
              <CardContent>
                {financials ? (
                  <Table>
                    <TableBody>
                      <TableRow><TableCell>Market Cap</TableCell><TableCell className="text-right">{formatCurrency(financials.marketCap)}</TableCell></TableRow>
                      <TableRow><TableCell>P/E Ratio</TableCell><TableCell className="text-right">{formatNumber(financials.peRatio)}</TableCell></TableRow>
                      <TableRow><TableCell>PEG Ratio</TableCell><TableCell className="text-right">{formatNumber(financials.pegRatio)}</TableCell></TableRow>
                      <TableRow><TableCell>EPS</TableCell><TableCell className="text-right">{formatNumber(financials.eps)}</TableCell></TableRow>
                      <TableRow><TableCell>Dividend Yield</TableCell><TableCell className="text-right">{formatPercentage(financials.dividendYield)}</TableCell></TableRow>
                      <TableRow><TableCell>Beta</TableCell><TableCell className="text-right">{formatNumber(financials.beta)}</TableCell></TableRow>
                      <TableRow><TableCell>52W High</TableCell><TableCell className="text-right">{formatNumber(financials.high52Week)}</TableCell></TableRow>
                      <TableRow><TableCell>52W Low</TableCell><TableCell className="text-right">{formatNumber(financials.low52Week)}</TableCell></TableRow>
                      <TableRow><TableCell>50D MA</TableCell><TableCell className="text-right">{formatNumber(financials.ma50Day)}</TableCell></TableRow>
                      <TableRow><TableCell>200D MA</TableCell><TableCell className="text-right">{formatNumber(financials.ma200Day)}</TableCell></TableRow>
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-muted-foreground">Financial data not available.</p>
                )}
              </CardContent>
            </Card>

            {/* Technicals Card */}
            <Card>
              <CardHeader>
                <CardTitle>Technicals</CardTitle>
              </CardHeader>
              <CardContent>
                {technicals ? (
                   <Table>
                     <TableBody>
                       <TableRow><TableCell>RSI (14 Day)</TableCell><TableCell className="text-right">{formatNumber(technicals.rsi)}</TableCell></TableRow>
                       <TableRow><TableCell>MACD</TableCell><TableCell className="text-right">{formatNumber(technicals.macd?.macd)}</TableCell></TableRow>
                       <TableRow><TableCell>MACD Signal</TableCell><TableCell className="text-right">{formatNumber(technicals.macd?.signal)}</TableCell></TableRow>
                       <TableRow><TableCell>MACD Hist</TableCell><TableCell className="text-right">{formatNumber(technicals.macd?.histogram)}</TableCell></TableRow>
                     </TableBody>
                   </Table>
                ) : (
                  <p className="text-muted-foreground">Technical data not available.</p>
                )}
              </CardContent>
            </Card>

            {/* Sentiment Card */}
            <Card>
              <CardHeader>
                <CardTitle>Sentiment</CardTitle>
              </CardHeader>
              <CardContent>
                {sentiment ? (
                   <Table>
                     <TableBody>
                       <TableRow><TableCell>News Sentiment</TableCell><TableCell className="text-right">{formatNumber(sentiment.newsSentimentScore)}</TableCell></TableRow>
                       <TableRow><TableCell>Social Trend</TableCell><TableCell className="text-right">{sentiment.socialMediaTrend ?? 'N/A'}</TableCell></TableRow>
                     </TableBody>
                   </Table>
                ) : (
                  <p className="text-muted-foreground">Sentiment data not available.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIHedgeFund; // Export the component
